export const MULTI_SELECT_CONFIRMED_PREFIX = '[multi-select-confirmed]';

export interface MultiSelectConfirmedPayload {
    responseKey: string;
    /**
     * When true, the answer is saved as member-notes context but never rendered
     * as a chat bubble (e.g. a `money-goal` deep-link answers the first question
     * for the member, so we must not pretend they saw/answered it in-thread).
     */
    silent?: boolean;
    selectedOptionIds: string[];
    inputText: string;
    /** Pre-resolved branch from deterministic option-ID mapping (welcome only). */
    resolvedBranch?: string;
    /**
     * The `WelcomeStartMenu` option id the member picked (welcome only). Carried
     * through so the opener intro can be looked up per selection (and survives
     * history restore); absent for free-text answers.
     */
    welcomeOptionId?: string;
}

export const buildMultiSelectConfirmedAgentMessage = ({
    displayText,
    payload,
}: {
    displayText: string;
    payload: MultiSelectConfirmedPayload;
}): string =>
    `${displayText}\n${MULTI_SELECT_CONFIRMED_PREFIX} ${JSON.stringify(payload)}`;

export const stripMultiSelectMachineLine = (message: string): string => {
    const prefixIndex = message.indexOf(MULTI_SELECT_CONFIRMED_PREFIX);
    if (prefixIndex === -1) {
        return message;
    }

    return message.slice(0, prefixIndex).trimEnd();
};

export const parseMultiSelectConfirmedMessage = (
    text: string,
): MultiSelectConfirmedPayload | null => {
    const prefixIndex = text.indexOf(MULTI_SELECT_CONFIRMED_PREFIX);
    if (prefixIndex === -1) {
        return null;
    }

    const jsonPayload = text
        .slice(prefixIndex + MULTI_SELECT_CONFIRMED_PREFIX.length)
        .trim();

    try {
        const parsed: unknown = JSON.parse(jsonPayload);
        if (
            typeof parsed !== 'object' ||
            parsed == null ||
            typeof (parsed as MultiSelectConfirmedPayload).responseKey !==
                'string' ||
            !Array.isArray(
                (parsed as MultiSelectConfirmedPayload).selectedOptionIds,
            ) ||
            typeof (parsed as MultiSelectConfirmedPayload).inputText !==
                'string'
        ) {
            return null;
        }

        return parsed as MultiSelectConfirmedPayload;
    } catch {
        return null;
    }
};

/** Returns the member-visible text for a stored multi-select confirmation message. */
export const formatMultiSelectUserMessageForDisplay = (
    text: string,
): string => {
    if (!text.includes(MULTI_SELECT_CONFIRMED_PREFIX)) {
        return text;
    }

    const displayText = stripMultiSelectMachineLine(text);
    if (displayText.trim() !== '') {
        return displayText;
    }

    const parsed = parseMultiSelectConfirmedMessage(text);
    // Silent answers are context-only — never surface their input as a bubble.
    if (parsed?.silent === true) {
        return '';
    }

    return parsed?.inputText ?? displayText;
};
